<template>
  <div class="page-container-v2">
    <a-spin :spinning="loading" tip="加载中...">
      <page-header
        :title="'公众号接入管理'"
        :sub-title="'注册并管理本公司微信公众号的接入信息（AppID、服务器配置等）'"
      >
      </page-header>

      <div class="main-container">
        <a-card :bordered="false">
          <div class="table-toolbar">
            <a-button type="primary" @click="openRegisterModal">注册新公众号</a-button>
          </div>

          <a-alert
            v-if="errorMsg"
            type="error"
            :message="errorMsg"
            show-icon
            style="margin-bottom: 12px"
          />

          <div v-if="!isMobileView">
            <a-table
              :data-source="accounts"
              :columns="columns"
              :pagination="false"
              row-key="gzhName"
              size="middle"
              :scroll="{ x: 1220 }"
            >
              <template #bodyCell="{ column, record }">
                <template v-if="column.key === 'displayName'">
                  <div>{{ record.displayName }}</div>
                  <div class="cell-sub">{{ record.gzhName }}</div>
                </template>
                <template v-else-if="column.key === 'serverConfig'">
                  <a-tag :color="record.hasToken ? 'green' : 'default'">Token</a-tag>
                  <a-tag :color="record.hasEncodingAesKey ? 'green' : 'default'">AESKey</a-tag>
                </template>
                <template v-else-if="column.key === 'status'">
                  <a-tag v-if="record.configComplete" color="green">已完成</a-tag>
                  <a-tag v-else color="orange">未完成（请重新注册补全）</a-tag>
                </template>
                <template v-else-if="column.key === 'action'">
                  <a-space>
                    <a-button size="small" :disabled="!record.configComplete" @click="openEditModal(record)">编辑</a-button>
                    <a-button
                      size="small"
                      :disabled="!record.configComplete"
                      :loading="testingGzhName === record.gzhName"
                      @click="testConnection(record.gzhName)"
                    >测试连接</a-button>
                  </a-space>
                  <div v-if="testResults[record.gzhName]" class="test-result" :class="{ 'is-ok': testResults[record.gzhName].ok, 'is-fail': !testResults[record.gzhName].ok }">
                    {{ testResults[record.gzhName].text }}
                  </div>
                </template>
              </template>
            </a-table>
          </div>

          <!-- 모바일 카드뷰 -->
          <div v-else class="mobile-card-list">
            <div v-for="record in accounts" :key="record.gzhName" class="mobile-company-card">
              <div class="card-field">
                <span class="card-label">公众号:</span>
                <span>{{ record.displayName }}<span class="cell-sub">（{{ record.gzhName }}）</span></span>
              </div>
              <div class="card-field">
                <span class="card-label">类型:</span>
                <span>{{ record.accountType }}</span>
              </div>
              <div class="card-field">
                <span class="card-label">AppID:</span>
                <span>{{ record.appId || '-' }}</span>
              </div>
              <div class="card-field">
                <span class="card-label">服务器配置:</span>
                <a-tag :color="record.hasToken ? 'green' : 'default'">Token</a-tag>
                <a-tag :color="record.hasEncodingAesKey ? 'green' : 'default'">AESKey</a-tag>
              </div>
              <div class="card-field">
                <span class="card-label">状态:</span>
                <a-tag v-if="record.configComplete" color="green">已完成</a-tag>
                <a-tag v-else color="orange">未完成（请重新注册补全）</a-tag>
              </div>
              <div v-if="testResults[record.gzhName]" class="test-result" :class="{ 'is-ok': testResults[record.gzhName].ok, 'is-fail': !testResults[record.gzhName].ok }">
                {{ testResults[record.gzhName].text }}
              </div>
              <div class="card-actions">
                <a-button size="small" :disabled="!record.configComplete" @click="openEditModal(record)">编辑</a-button>
                <a-button
                  size="small"
                  :disabled="!record.configComplete"
                  :loading="testingGzhName === record.gzhName"
                  @click="testConnection(record.gzhName)"
                >测试连接</a-button>
              </div>
            </div>
            <div v-if="loaded && accounts.length === 0" style="text-align: center; color: #999; padding: 24px;">
              尚未注册公众号，请点击上方按钮注册
            </div>
          </div>

          <a-empty v-if="!isMobileView && loaded && accounts.length === 0" description="尚未注册公众号，请点击上方按钮注册" style="margin: 24px 0" />
        </a-card>
      </div>

      <!-- 등록/수정 모달 -->
      <a-modal
        v-model:open="modalOpen"
        :title="isEdit ? '编辑公众号接入信息' : '注册新公众号'"
        :confirm-loading="saving"
        :width="isMobileView ? '100%' : 640"
        :z-index="isMobileView ? 1100 : undefined"
        wrap-class-name="gzh-account-overlay"
        @ok="submit"
        @cancel="closeModal"
        okText="保存"
        cancelText="取消"
      >
        <a-form layout="vertical">
          <a-form-item required>
            <template #label>逻辑名称（gzhName）<field-help field="gzhName" /><span class="label-hint">— 系统内部唯一标识，注册后不可修改</span></template>
            <a-input
              v-model:value="form.gzhName"
              :disabled="isEdit"
              placeholder="例: thermage-china-dyh（小写字母/数字/连字符）"
            />
          </a-form-item>
          <a-form-item required>
            <template #label>显示名称<field-help field="displayName" /></template>
            <a-input v-model:value="form.displayName" placeholder="例: 舒美 订阅号" />
          </a-form-item>
          <a-form-item required>
            <template #label>账号类型<field-help field="accountType" /></template>
            <a-select v-model:value="form.accountType" style="width: 200px">
              <a-select-option value="订阅号">订阅号</a-select-option>
              <a-select-option value="服务号">服务号</a-select-option>
            </a-select>
          </a-form-item>
          <a-form-item required>
            <template #label>AppID<field-help field="appId" /></template>
            <a-input v-model:value="form.appId" placeholder="wx 开头，微信公众平台 → 设置与开发 → 基本配置" />
          </a-form-item>
          <a-form-item :required="!isEdit">
            <template #label>AppSecret<field-help field="appSecret" /><span v-if="isEdit" class="label-hint">{{ secretsLoaded ? '— 已加载保存值（点击眼睛查看）' : `— 留空则保持不变（已保存: ${editingMasked}）` }}</span></template>
            <a-input-password v-model:value="form.appSecret" :placeholder="isEdit ? '留空则保持不变' : '微信公众平台 → 基本配置 → 开发者密码'" />
          </a-form-item>
          <a-form-item>
            <template #label>服务器配置 Token<field-help field="token" /><span v-if="isEdit" class="label-hint">{{ secretsLoaded ? '— 已加载保存值（点击眼睛查看）' : '— 留空则保持不变' }}</span></template>
            <a-input-password v-model:value="form.token" placeholder="微信公众平台 → 基本配置 → 服务器配置 Token" />
          </a-form-item>
          <a-form-item>
            <template #label>EncodingAESKey（43位）<field-help field="encodingAesKey" /><span v-if="isEdit" class="label-hint">{{ secretsLoaded ? '— 已加载保存值（点击眼睛查看）' : '— 留空则保持不变' }}</span></template>
            <a-input-password v-model:value="form.encodingAesKey" placeholder="服务器配置 EncodingAESKey（安全模式必需）" />
          </a-form-item>
          <a-form-item>
            <template #label>原始ID（gh_ 开头）<field-help field="gzhUserId" /></template>
            <a-input v-model:value="form.gzhUserId" placeholder="例: gh_2b8bc89b14cf（公众号设置 → 账号详情）" />
          </a-form-item>
        </a-form>

        <!-- 위챗 콘솔에서 해야 하는 후속 설정 안내 -->
        <a-alert type="info" show-icon>
          <template #message>保存后请在微信公众平台完成以下设置</template>
          <template #description>
            <ol class="guide-list">
              <li>
                服务器配置 URL:
                <code>{{ callbackUrl }}</code>
                <span class="label-hint">（消息回调地址，填入微信「服务器配置」的 URL 处）</span>
              </li>
              <li>消息加解密方式: <b>安全模式</b>，Token/EncodingAESKey 与上方填写值一致</li>
              <li>IP白名单: 添加服务器出口IP（未添加时菜单发布会报 40164 — 可用「测试连接」诊断）</li>
            </ol>
          </template>
        </a-alert>
      </a-modal>
    </a-spin>
  </div>
</template>

<script>
import PageHeader from '@/views/components/PageHeader.vue';
import GzhFieldHelp from './components/GzhFieldHelp.vue';
import gzhApis from './api';
import { oneBtnDialog } from '@/utils/utils';
import dealerEcountListMixin from '@/mixins/dealerEcountListMixin';

const EMPTY_FORM = {
  gzhName: '',
  displayName: '',
  accountType: '订阅号',
  appId: '',
  appSecret: '',
  token: '',
  encodingAesKey: '',
  gzhUserId: '',
};

// 신규 공중계정 콜백(v2 멀티테넌트, ?gzh=<gzhName>). uniCloud(miniapp-functions) 서비스공간 도메인.
// 공개 콜백 URL(위챗이 호출) — 비밀값 아님. 도메인 변경 시 이 상수만 갱신.
const CALLBACK_BASE = 'https://fc-mp-07af775c-d653-4e72-8732-b6d74c71ef44.next.bspapp.com/gzh-event-msg-v2';

export default {
  name: 'GzhAccountManagement',
  mixins: [dealerEcountListMixin],
  components: { PageHeader, FieldHelp: GzhFieldHelp },
  data() {
    return {
      apis: null,
      loading: false,
      loaded: false,
      saving: false,
      errorMsg: '',
      accounts: [],
      modalOpen: false,
      isEdit: false,
      // 편집 모달에서 저장 비밀값 프리필 성공 여부 — 실패 시 기존 '빈값=유지' UX로 폴백
      secretsLoaded: false,
      editingMasked: '',
      form: { ...EMPTY_FORM },
      testingGzhName: '',
      testResults: {},
      columns: [
        { title: '公众号', key: 'displayName', width: 220 },
        { title: '类型', dataIndex: 'accountType', key: 'accountType', width: 90 },
        { title: 'AppID', dataIndex: 'appId', key: 'appId', width: 190 },
        { title: '原始ID', dataIndex: 'gzhUserId', key: 'gzhUserId', width: 150 },
        { title: 'AppSecret', dataIndex: 'appSecretMasked', key: 'appSecretMasked', width: 120 },
        { title: '服务器配置', key: 'serverConfig', width: 140 },
        { title: '状态', key: 'status', width: 170 },
        { title: '操作', key: 'action' },
      ],
    };
  },
  computed: {
    // 등록 폼에 안내할 위챗 서버설정 콜백 URL(gzhName 입력 시 실시간 반영)
    callbackUrl() {
      const name = this.form.gzhName || '<逻辑名称>';
      return `${CALLBACK_BASE}?gzh=${name}`;
    },
  },
  created() {
    this.apis = gzhApis();
    this.fetchAccounts();
  },
  methods: {
    async fetchAccounts() {
      try {
        this.loading = true;
        this.errorMsg = '';
        const response = await this.apis.gzhAccounts.getList();
        this.accounts = response.data?.accounts || [];
      } catch (error) {
        console.error('Error fetching gzh accounts:', error);
        this.errorMsg = '获取公众号列表失败: ' + (error.response?.data?.message || error.message);
      } finally {
        this.loading = false;
        this.loaded = true;
      }
    },

    openRegisterModal() {
      this.isEdit = false;
      this.secretsLoaded = false;
      this.editingMasked = '';
      this.form = { ...EMPTY_FORM };
      this.modalOpen = true;
    },

    openEditModal(record) {
      this.isEdit = true;
      this.secretsLoaded = false;
      this.editingMasked = record.appSecretMasked || '(未保存)';
      this.form = {
        gzhName: record.gzhName,
        displayName: record.displayName,
        accountType: record.accountType,
        appId: record.appId,
        appSecret: '',
        token: '',
        encodingAesKey: '',
        gzhUserId: record.gzhUserId,
      };
      this.modalOpen = true;
      // 저장 비밀값 프리필 — a-input-password 마스킹 상태로 채우고 눈동자로 확인.
      // 조회 실패 시 조용히 기존 UX(빈값=유지)로 폴백한다. 같은 값 재저장은 서버상 무해(멱등).
      this.loadSavedSecrets(record.gzhName);
    },

    async loadSavedSecrets(gzhName) {
      try {
        const { data } = await this.apis.gzhAccounts.getSecrets(gzhName);
        // 모달이 닫혔거나 다른 계정으로 전환된 뒤 도착한 응답은 무시(레이스 가드)
        if (!this.modalOpen || !this.isEdit || this.form.gzhName !== gzhName) return;
        this.form.appSecret = data.appSecret || '';
        this.form.token = data.token || '';
        this.form.encodingAesKey = data.encodingAesKey || '';
        this.secretsLoaded = true;
      } catch (e) {
        // 폴백: 프리필 없이 기존 쓰기전용 UX 유지 (표시만 실패, 편집엔 지장 없음)
        this.secretsLoaded = false;
      }
    },

    closeModal() {
      this.modalOpen = false;
    },

    validateForm() {
      const f = this.form;
      if (!this.isEdit) {
        if (!/^[a-z0-9][a-z0-9-]{2,49}$/.test(f.gzhName)) {
          return '逻辑名称须为小写字母/数字/连字符（3~50位），例: thermage-china-dyh';
        }
        if (!f.appSecret) return '请填写 AppSecret';
      }
      if (!f.displayName) return '请填写显示名称';
      if (f.appId && !/^wx[0-9a-z]+$/.test(f.appId)) return 'AppID 格式不正确（wx 开头小写字母/数字）';
      if (!this.isEdit && !f.appId) return '请填写 AppID';
      if (f.encodingAesKey && f.encodingAesKey.length !== 43) return 'EncodingAESKey 须为43位';
      if (f.gzhUserId && !/^gh_[0-9a-z]+$/.test(f.gzhUserId)) return '原始ID 格式不正确（gh_ 开头）';
      return '';
    },

    async submit() {
      const validationError = this.validateForm();
      if (validationError) {
        await oneBtnDialog('提示', validationError, false);
        return;
      }
      try {
        this.saving = true;
        if (this.isEdit) {
          // 빈 비밀값은 그대로 보내도 서버가 "유지"로 해석하지만, 최소 전송 원칙으로 제외
          const payload = {
            displayName: this.form.displayName,
            accountType: this.form.accountType,
            appId: this.form.appId,
            gzhUserId: this.form.gzhUserId || undefined,
          };
          if (this.form.appSecret) payload.appSecret = this.form.appSecret;
          if (this.form.token) payload.token = this.form.token;
          if (this.form.encodingAesKey) payload.encodingAesKey = this.form.encodingAesKey;
          await this.apis.gzhAccounts.update(this.form.gzhName, payload);
        } else {
          const payload = {
            gzhName: this.form.gzhName,
            displayName: this.form.displayName,
            accountType: this.form.accountType,
            appId: this.form.appId,
            appSecret: this.form.appSecret,
          };
          if (this.form.token) payload.token = this.form.token;
          if (this.form.encodingAesKey) payload.encodingAesKey = this.form.encodingAesKey;
          if (this.form.gzhUserId) payload.gzhUserId = this.form.gzhUserId;
          await this.apis.gzhAccounts.register(payload);
        }
        this.modalOpen = false;
        // 화면 상태 유지 — reload 트리거(3번째 인자 true) 금지
        await oneBtnDialog('成功', this.isEdit ? '公众号接入信息已更新' : '公众号注册成功，请完成微信公众平台服务器配置', false);
        await this.fetchAccounts();
      } catch (error) {
        console.error('Error saving gzh account:', error);
        const message = error.response?.data?.message || error.message;
        await oneBtnDialog('失败', message, false);
      } finally {
        this.saving = false;
      }
    },

    async testConnection(gzhName) {
      try {
        this.testingGzhName = gzhName;
        const response = await this.apis.gzhAccounts.testConnection(gzhName);
        const result = response.data || {};
        const text = result.ok
          ? '连接成功（access_token 获取正常）'
          : `连接失败${result.errcode ? ` (errcode ${result.errcode})` : ''}: ${result.friendlyMessage || result.errmsg || '未知错误'}`;
        // Vue3 — 직접 할당으로 반응성 유지($set 없음)
        this.testResults = { ...this.testResults, [gzhName]: { ok: !!result.ok, text } };
      } catch (error) {
        console.error('Error testing gzh connection:', error);
        this.testResults = {
          ...this.testResults,
          [gzhName]: { ok: false, text: '诊断请求失败: ' + (error.response?.data?.message || error.message) },
        };
      } finally {
        this.testingGzhName = '';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.table-toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
}
.cell-sub {
  color: #999;
  font-size: 12px;
}
.label-hint {
  color: #999;
  font-size: 12px;
  margin-left: 6px;
  font-weight: normal;
}
.test-result {
  margin-top: 6px;
  font-size: 12px;
  &.is-ok { color: #52c41a; }
  &.is-fail { color: #ff4d4f; }
}
.guide-list {
  margin: 4px 0 0 16px;
  padding: 0;
  li { margin-bottom: 4px; }
  code {
    background: rgba(0, 0, 0, 0.06);
    padding: 1px 4px;
    border-radius: 3px;
    word-break: break-all;
  }
}
@media (max-width: 767px) {
  .mobile-card-list { display: flex; flex-direction: column; gap: 12px; }
  .mobile-company-card { border: 1px solid #f0f0f0; border-radius: 6px; padding: 12px; background: #fff; }
  .card-field { display: flex; gap: 8px; margin-bottom: 6px; font-size: 13px; align-items: center; flex-wrap: wrap; }
  .card-label { font-weight: 600; min-width: 90px; flex-shrink: 0; color: #666; }
  .card-actions { display: flex; flex-wrap: wrap; gap: 6px; border-top: 1px solid #f5f5f5; padding-top: 8px; }
  .card-actions :deep(.ant-btn) { flex: 1 1 auto; }
}
</style>

<!-- 모달은 document.body로 teleport되므로 scoped 스타일이 닿지 않는다(house precedent: IamConsole.vue).
     wrap-class-name으로 부여한 .gzh-account-overlay로 스코프를 좁혀 다른 화면에 영향이 없도록 한다. -->
<style>
@media (max-width: 767px) {
  .ant-modal-wrap.gzh-account-overlay {
    z-index: 1100;
  }

  .ant-modal-wrap.gzh-account-overlay ~ .ant-modal-mask {
    z-index: 1100;
  }

  .gzh-account-overlay .ant-modal {
    max-width: calc(100vw - 24px);
    margin: 8px auto;
  }

  .gzh-account-overlay .ant-modal-body {
    max-height: 70vh;
    max-height: 70dvh;
    overflow-y: auto;
  }
}
</style>
